import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Add, Close, Remove } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { AccountData, ApiError, PassOut, PassService } from '../../../../api';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { useLanguage } from '../../../../common/GeneralUtils';
import { getPassOrderPage } from '../../../../utils/AccountUtils';
import { useApiResponseHandlerContext } from '../../../../common/apiNotifications/useApiResponseHandlerContext';
import { ApiResponseHandlerType } from '../../../../common/apiNotifications/ApiResponseHandlerContext';
import { formatSignedNumber } from '../../utils';

const STATUS_ERROR_DETAIL = 'pass is not active or paused';
const REASON_MAX_LENGTH = 500;

type Props = {
  open: boolean;
  onClose: () => void;
  pass: PassOut;
  totalPunches: number;
  onUpdated: (pass: PassOut) => void;
};

export const AdjustPunchesDialog = ({ open, onClose, pass, totalPunches, onUpdated }: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isFullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { showResponseNotification } = useApiResponseHandlerContext();

  const [delta, setDelta] = useState(0);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setDelta(0);
    setReason('');
    setReasonError(false);
    setSubmitting(false);
    setApiError(null);
  }, [open]);

  const used = pass.redeemed_order_ids?.length || 0;
  const minDelta = used - totalPunches;
  const passOrderPage = getPassOrderPage(account, pass.order_page_id);
  const passLabel = passOrderPage?.label?.[language] || passOrderPage?.label?.en || '';
  const subtitle = [passLabel, `${pass.firstname} ${pass.lastname}`.trim()].filter(Boolean).join(' · ');
  const stepperButtonSize = isFullScreen ? 48 : 44;
  const title = t('passes.adjust_punches.title');

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setReasonError(true);
      return;
    }
    setSubmitting(true);
    setApiError(null);
    try {
      const updated = await PassService.adjustPassPunches(pass.id, { delta, reason: reason.trim() });
      onUpdated(updated);
      showResponseNotification(t('passes.adjust_punches.success'), ApiResponseHandlerType.SUCCESS);
      onClose();
    } catch (e) {
      const isStatusError = e instanceof ApiError && e.status === 409 && e.body?.detail === STATUS_ERROR_DETAIL;
      setApiError(isStatusError ? t('passes.adjust_punches.error_status') : t('saveHeader.errorGeneric'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitButton = (
    <LoadingButton
      variant="contained"
      size={isFullScreen ? 'large' : 'medium'}
      fullWidth={isFullScreen}
      loading={submitting}
      disabled={delta === 0}
      onClick={handleSubmit}>
      {t('passes.adjust_punches.submit')}
    </LoadingButton>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth fullScreen={isFullScreen}>
      {isFullScreen ? (
        <Grid container alignItems="center" flexWrap="nowrap" gap={1} px={1} py={1}>
          <IconButton aria-label={t('close')} onClick={onClose}>
            <Close />
          </IconButton>
          <Typography variant="h6" component="h2">
            {title}
          </Typography>
        </Grid>
      ) : (
        <DialogTitle>{title}</DialogTitle>
      )}
      <DialogContent sx={isFullScreen ? { pt: 2 } : undefined}>
        <Grid container flexDirection="column" gap={2.5}>
          {apiError && <Alert severity="error">{apiError}</Alert>}
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
          <Typography variant="body1">
            {t('passes.adjust_punches.current', { total: totalPunches, used, remaining: totalPunches - used })}
          </Typography>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
              {t('passes.adjust_punches.change_label')}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton
                aria-label={t('passes.adjust_punches.remove_one')}
                disabled={delta <= minDelta}
                onClick={() => setDelta((current) => current - 1)}
                sx={{
                  width: stepperButtonSize,
                  height: stepperButtonSize,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}>
                <Remove />
              </IconButton>
              <Box
                dir="ltr"
                sx={{
                  width: isFullScreen ? undefined : 88,
                  flex: isFullScreen ? 1 : undefined,
                  height: stepperButtonSize,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatSignedNumber(delta)}
                </Typography>
              </Box>
              <IconButton
                aria-label={t('passes.adjust_punches.add_one')}
                onClick={() => setDelta((current) => current + 1)}
                sx={{
                  width: stepperButtonSize,
                  height: stepperButtonSize,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                }}>
                <Add />
              </IconButton>
            </Box>
          </Box>
          {delta === minDelta && (
            <Typography variant="caption" color="text.secondary">
              {t('passes.adjust_punches.min_reached', { used })}
            </Typography>
          )}
          <Typography variant="subtitle2">
            {t('passes.adjust_punches.after', {
              total: totalPunches + delta,
              remaining: totalPunches + delta - used,
            })}
          </Typography>
          <TextField
            multiline
            minRows={3}
            required
            label={t('passes.adjust_punches.reason_label')}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
              if (reasonError) setReasonError(false);
            }}
            inputProps={{ maxLength: REASON_MAX_LENGTH }}
            error={reasonError}
            helperText={
              reasonError ? t('passes.adjust_punches.reason_required') : t('passes.adjust_punches.reason_helper')
            }
          />
        </Grid>
      </DialogContent>
      <DialogActions sx={isFullScreen ? { p: 2 } : undefined}>
        {!isFullScreen && (
          <Button onClick={onClose} disabled={submitting}>
            {t('cancel')}
          </Button>
        )}
        {submitButton}
      </DialogActions>
    </Dialog>
  );
};
